import clsx from 'clsx';
import { countLabel, formatPreviewValue } from '../core/_utils';
import { type ExportFieldDef, type ExportFormat } from '../types';

const PREVIEW_ROWS = 3;

const FORMAT_ICON: Record<ExportFormat, string> = {
  pdf: 'bi-file-earmark-pdf text-danger',
  xlsx: 'bi-file-earmark-spreadsheet text-success',
  csv: 'bi-filetype-csv text-primary',
};

type Props = {
  title: string;
  format: ExportFormat;
  /** Só em PDF. */
  orientationLabel?: string;
  /** Colunas do arquivo, na ordem do arquivo. */
  fields: ExportFieldDef[];
  headerOf: (key: string) => string;
  /** Linhas reais que o escopo escolhido tem à mão (a página, ou as marcadas). */
  sampleRows: Record<string, unknown>[];
  /** Total de linhas do escopo, para o rodapé. */
  totalRows: number;
};

/** Prévia do arquivo com dados reais da tela. É só prévia: o arquivo quem gera é o backend (ou o exportador do navegador). */
const ExportFilePreview = ({ title, format, orientationLabel, fields, headerOf, sampleRows, totalRows }: Props) => {
  const sample = sampleRows.slice(0, PREVIEW_ROWS);
  const remaining = totalRows - sample.length;

  return (
    <section aria-label="Prévia do arquivo" className="card shadow-sm overflow-hidden flex-shrink-0">
      <div className="d-flex align-items-center gap-2 px-4 py-3 border-bottom">
        <i className={clsx('bi fs-3', FORMAT_ICON[format])} />
        <span className="fw-bold fs-7 text-gray-800 text-truncate">
          {title}.{format}
        </span>
        {orientationLabel ? <span className="badge badge-light ms-auto">{orientationLabel}</span> : null}
      </div>
      {fields.length === 0 ? (
        <div className="text-center text-gray-600 fs-7 py-8">Inclua ao menos uma coluna.</div>
      ) : (
        // Com muitas colunas a tabela rola na horizontal dentro do cartão.
        <div className="table-responsive">
          <table className="table table-sm table-row-dashed fs-9 mb-0 text-nowrap">
            <thead>
              <tr className="fw-bold text-gray-600 bg-light">
                {fields.map((field) => (
                  <th key={field.key} className="px-3">
                    {headerOf(field.key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sample.map((row, index) => (
                // eslint-disable-next-line react/no-array-index-key -- linha de prévia não tem identidade nem muda de ordem
                <tr key={index}>
                  {fields.map((field) => (
                    <td key={field.key} className="px-3 text-gray-700">
                      {formatPreviewValue(row, field)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {remaining > 0 ? (
        <div className="px-4 py-2 fs-9 text-gray-500 border-top">
          … e mais {countLabel(remaining, 'linha', 'linhas')}
        </div>
      ) : null}
    </section>
  );
};

export default ExportFilePreview;
